import DescriptionEdit from './DescriptionEdit';
import { TagsComponent } from './TagsComponent';
import type {
  Log,
  MessageEntry,
  MessageLog,
  NewDayLog,
  RadioShiftLog,
  VisibleLog,
} from '@/data/logs';
import { $currentOperator, $defaultFrequency, $tags, $view } from '@/stores';
import { logs } from '@/stores/logbook';
import { alert, confirm } from '@/utils/alertUtils';
import { formatTime, parseTime } from '@/utils/dataUtils';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

function DeleteNum({ log, num }: { log: Log; num: number | string }) {
  const handleDelete = async () => {
    if (
      await confirm({
        title: 'Delete Log',
        message: 'Are you sure you want to delete this log?',
      })
    ) {
      logs.deleteLog(log.id, $currentOperator.get());
    }
  };

  return (
    <>
      <button
        className="group w-full cursor-pointer print:hidden"
        onClick={handleDelete}
        title="Delete Log"
      >
        <span className="group-hover:hidden group-focus:hidden">{num}</span>
        <div className="hidden rounded bg-red-300 font-bold text-red-950 group-hover:block group-focus:block">
          &times;
        </div>
      </button>
      <div className="hidden print:block">{num}</div>
    </>
  );
}

function UndraftNum({ log, num }: { log: Log; num: number | string }) {
  const handleUndraft = () => {
    logs.undraftLog(log.id);
  };

  return (
    <>
      <button
        className="group w-full cursor-pointer print:hidden"
        onClick={handleUndraft}
        title="Undraft Log"
      >
        <span className="group-hover:hidden group-focus:hidden">{num}</span>
        <div className="hidden rounded bg-green-300 font-bold text-green-950 group-hover:block group-focus:block">
          +
        </div>
      </button>
      <div className="relative hidden print:block">
        <div className="absolute inset-0 -top-3.5 flex items-center justify-center text-sm font-bold opacity-50">
          Draft
        </div>
        <div className="translate-y-2">{num}</div>
      </div>
    </>
  );
}

function MessageLogComponent({
  log,
  num,
  index,
}: {
  log: MessageLog;
  num: number;
  index: number;
}) {
  const entry = log.entries.at(-1)!;

  const tags = useStore($tags);
  const view = useStore($view);
  const operator = useStore($currentOperator);
  const defaultFrequency = useStore($defaultFrequency);
  const [frequency, setFrequency] = useState(entry.frequency);
  const [callSignFrom, setCallSignFrom] = useState(entry.callSign.from);
  const [callSignTo, setCallSignTo] = useState(entry.callSign.to);
  const [description, setDescription] = useState(entry.description);
  const [time, setTime] = useState(() => formatTime(entry.time));
  const [tagIds, setTagIds] = useState<string[]>(log.tags || []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFrequency(entry.frequency);
    setCallSignFrom(entry.callSign.from);
    setCallSignTo(entry.callSign.to);
    setDescription(entry.description);
    setTime(formatTime(entry.time));
    setTagIds(log.tags || []);
  }, [
    entry.callSign.from,
    entry.callSign.to,
    entry.description,
    entry.frequency,
    entry.time,
    log.entries.length,
    log.tags,
  ]);

  let bgColor = '';

  if (tagIds.length > 0) {
    const tag = tags.find(t => t.id === tagIds[0]);

    if (tag) {
      bgColor = tag.color + '33';
    }
  }

  const saveFrequency = () => {
    if (frequency === entry.frequency) {
      return;
    }

    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignFrom, to: callSignTo },
      time: entry.time,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
  };

  const saveCallSignFrom = (callSignFrom: string) => {
    if (callSignFrom === entry.callSign.from) {
      return;
    }

    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignFrom, to: callSignTo },
      time: entry.time,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
  };

  const saveCallSignTo = (callSignTo: string) => {
    if (callSignTo === entry.callSign.to) {
      return;
    }

    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignFrom, to: callSignTo },
      time: entry.time,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
  };

  const swapCallSigns = () => {
    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignTo, to: callSignFrom },
      time: entry.time,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
  };

  const saveTime = () => {
    if (!log.draft && time.trim() === '') {
      setTime(formatTime(entry.time));
      return;
    }
    const parsedTime = parseTime(time, log.draft);

    if (parsedTime === null) {
      alert({
        title: 'Error',
        message: 'Invalid time format',
      });
      return;
    }

    if (parsedTime === entry.time) {
      return;
    }

    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignFrom, to: callSignTo },
      time: parsedTime,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
    setTime(formatTime(parsedTime));
  };

  const saveDescription = () => {
    if (description === entry.description) {
      return;
    }

    const newEntry: MessageEntry = {
      timestamp: Date.now(),
      frequency,
      callSign: { from: callSignFrom, to: callSignTo },
      time: entry.time,
      description,
      operator,
    };

    logs.addEntry(log.id, newEntry);
  };

  const saveTags = (newTagIds: string[]) => {
    if (
      newTagIds.length === tagIds.length &&
      newTagIds.every(id => tagIds.includes(id))
    ) {
      return;
    }

    logs.setTags(log.id, newTagIds);
    setTagIds(newTagIds);
  };

  switch (view) {
    case 'operator':
      return (
        <tr
          className={clsx('break-inside-avoid', log.draft && 'bg-yellow-100')}
          style={bgColor ? { backgroundColor: bgColor } : {}}
        >
          <td className="border border-gray-600 text-nowrap">
            <input
              type="text"
              value={frequency}
              onChange={e => setFrequency(e.target.value)}
              onBlur={saveFrequency}
              placeholder={defaultFrequency}
              className="w-full py-2 text-center"
            />
          </td>
          <td className="border border-gray-600 text-center text-nowrap">
            <div className="flex items-center justify-center">
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={e => {
                  const newValue = e.currentTarget.textContent || '';
                  setCallSignFrom(newValue);
                  saveCallSignFrom(newValue);
                }}
                className="min-h-6 px-2 text-center"
              >
                {callSignFrom}
              </div>
              <div className="cursor-pointer" onClick={swapCallSigns}>
                →
              </div>
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={e => {
                  const newValue = e.currentTarget.textContent || '';
                  setCallSignTo(newValue);
                  saveCallSignTo(newValue);
                }}
                className="min-h-6 px-2 text-center"
              >
                {callSignTo}
              </div>
            </div>
          </td>
          <td className="border border-gray-600 text-center">
            <input
              type="text"
              value={time}
              onChange={e => setTime(e.target.value)}
              onBlur={saveTime}
              className="w-full py-2 text-center print:hidden"
            />
            <div className="hidden py-2 text-center print:block">
              {log.draft ? 'Draft' : formatTime(entry.time)}
            </div>
          </td>
          <td className="border border-gray-600">
            <DescriptionEdit
              description={description}
              setDescription={setDescription}
              onBlur={saveDescription}
            />
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center text-nowrap">
            {log.draft ? (
              <UndraftNum log={log} num={num} />
            ) : (
              <DeleteNum log={log} num={num} />
            )}
          </td>
          <td className="border border-gray-600 text-center print:hidden">
            <TagsComponent
              tagIds={tagIds}
              addTag={tag => {
                if (!tagIds.includes(tag.id)) {
                  const newTagIds = [...tagIds, tag.id];
                  saveTags(newTagIds);
                }
              }}
              removeTag={tagId => {
                const newTagIds = tagIds.filter(id => id !== tagId);
                saveTags(newTagIds);
              }}
            />
          </td>
        </tr>
      );
    case 'officer':
      return (
        <tr
          className={clsx('break-inside-avoid', log.draft && 'bg-yellow-100')}
          style={bgColor ? { backgroundColor: bgColor } : {}}
        >
          <td className="border border-gray-600 text-nowrap">
            <div className="w-full py-2 text-center">{frequency}</div>
          </td>
          <td className="border border-gray-600 text-center text-nowrap">
            <div className="flex items-center justify-center">
              <div className="min-h-6 px-2 text-center">{callSignFrom}</div>
              <div>→</div>
              <div className="min-h-6 px-2 text-center">{callSignTo}</div>
            </div>
          </td>
          <td className="border border-gray-600 text-center">
            <div className="w-full py-2 text-center">
              {log.draft ? 'Draft' : formatTime(entry.time)}
            </div>
          </td>
          <td className="border border-gray-600">
            <DescriptionEdit readonly description={description} />
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center text-nowrap">
            {num}
          </td>
          <td className="border border-gray-600 text-center print:hidden">
            <TagsComponent tagIds={tagIds} />
          </td>
        </tr>
      );
    case 'audit':
      return log.entries.map((entry, idx) => (
        <tr
          className={clsx(
            'break-inside-avoid',
            log.draft && 'bg-yellow-100',
            index % 2 === 0 && 'bg-gray-200',
          )}
        >
          <td className="border border-gray-600 text-nowrap">
            <div className="w-full py-2 text-center">{entry.frequency}</div>
          </td>
          <td className="border border-gray-600 text-center text-nowrap">
            <div className="flex items-center justify-center">
              <div className="min-h-6 px-2 text-center">
                {entry.callSign.from}
              </div>
              <div>→</div>
              <div className="min-h-6 px-2 text-center">
                {entry.callSign.to}
              </div>
            </div>
          </td>
          <td className="border border-gray-600 text-center">
            <div className="w-full py-2 text-center">
              {new Date(entry.timestamp).toISOString()}
            </div>
          </td>
          <td className="border border-gray-600 text-center">
            <div className="w-full py-2 text-center">
              {formatTime(entry.time)}
            </div>
          </td>
          <td className="border border-gray-600">
            <DescriptionEdit readonly description={entry.description} />
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center text-nowrap">
            {num}
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center text-nowrap">
            {idx + 1}
          </td>
        </tr>
      ));
  }
}

function RadioShiftLogComponent({
  log,
  index,
}: {
  log: RadioShiftLog;
  index: number;
}) {
  const entry = log.entries.at(-1)!;

  const view = useStore($view);
  const operator = useStore($currentOperator);
  const [notes, setNotes] = useState(entry.notes);
  const [time, setTime] = useState(() => formatTime(entry.time));

  switch (view) {
    case 'operator':
      return (
        <tr
          className={clsx('break-inside-avoid', log.draft && 'bg-yellow-100')}
        >
          <td className="border border-gray-600 px-4 py-2 text-nowrap">-</td>
          <td className="border border-gray-600 px-4 py-2 text-center">-</td>
          <td className="border border-gray-600">
            <input
              type="text"
              value={time}
              onChange={e => setTime(e.target.value)}
              onBlur={() => {
                const parsedTime = parseTime(time);
                if (parsedTime === null) {
                  alert({
                    title: 'Error',
                    message: 'Invalid time format',
                  });
                  return;
                }

                if (parsedTime === entry.time) {
                  return;
                }

                const newEntry = {
                  timestamp: Date.now(),
                  time: parsedTime,
                  notes,
                  oldOperator: entry.oldOperator,
                  newOperator: entry.newOperator,
                  operator,
                };

                logs.addEntry(log.id, newEntry);
                setTime(formatTime(parsedTime));
              }}
              className="w-full py-2 text-center"
            />
          </td>
          <td className="border border-gray-600">
            <DescriptionEdit
              description={notes}
              setDescription={setNotes}
              onBlur={() => {
                if (notes === entry.notes) {
                  return;
                }

                const newEntry = {
                  timestamp: Date.now(),
                  time: entry.time,
                  notes,
                  oldOperator: entry.oldOperator,
                  newOperator: entry.newOperator,
                  operator,
                };

                logs.addEntry(log.id, newEntry);
              }}
            />
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center text-nowrap">
            {log.draft ? (
              <UndraftNum log={log} num="-" />
            ) : (
              <DeleteNum log={log} num="-" />
            )}
          </td>
          <td className="border border-gray-600 text-center print:hidden">-</td>
        </tr>
      );
    case 'officer':
      return (
        <tr
          className={clsx('break-inside-avoid', log.draft && 'bg-yellow-100')}
        >
          <td className="border border-gray-600 px-4 py-2 text-nowrap">-</td>
          <td className="border border-gray-600 px-4 py-2 text-center">-</td>
          <td className="border border-gray-600 px-4 py-2">{time}</td>
          <td className="border border-gray-600 px-4 py-2">{notes}</td>
          <td className="border border-gray-600 px-4 py-2 text-center">-</td>
          <td className="border border-gray-600 text-center print:hidden">-</td>
        </tr>
      );
    case 'audit':
      return log.entries.map((entry, idx) => (
        <tr
          className={clsx(
            'break-inside-avoid',
            log.draft && 'bg-yellow-100',
            index % 2 === 0 && 'bg-gray-200',
          )}
        >
          <td className="border border-gray-600 px-4 py-2 text-nowrap">-</td>
          <td className="border border-gray-600 px-4 py-2 text-center">-</td>
          <td className="border border-gray-600 px-4 py-2 text-center">
            {new Date(entry.timestamp).toISOString()}
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center">
            {time}
          </td>
          <td className="border border-gray-600">
            <DescriptionEdit readonly description={entry.notes} />
          </td>
          <td className="border border-gray-600 px-4 py-2 text-center">-</td>
          <td className="border border-gray-600 px-4 py-2 text-center">
            {idx + 1}
          </td>
        </tr>
      ));
  }
}

function NewDayLogComponent({ log, index }: { log: NewDayLog; index: number }) {
  return (
    <tr className="break-inside-avoid bg-gray-200">
      <td className="border border-gray-600 px-4 py-2 text-center">-</td>
      <td className="border border-gray-600 px-4 py-2 text-center">-</td>
      <td className="border border-gray-600 px-4 py-2 text-center">-</td>
      <td className="border border-gray-600 px-4 py-2 text-center">
        {log.day}
      </td>
      <td className="border border-gray-600 px-4 py-2 text-center">-</td>
      <td className="border border-gray-600 px-4 py-2 text-center print:hidden">
        -
      </td>
    </tr>
  );
}

export default function LogComponent({
  log,
  num,
  index,
}: {
  log: VisibleLog;
  num: number;
  index: number;
}) {
  if ('entries' in log && log.entries.length === 0) {
    return null;
  }
  switch (log.type) {
    case 'message':
      return <MessageLogComponent log={log} num={num} index={index} />;
    case 'radioShift':
      return <RadioShiftLogComponent log={log} index={index} />;
    case 'newDay':
      return <NewDayLogComponent log={log} index={index} />;
  }
}
